const SESSION_MODE_KEY = "auth_session_mode";
const LOGIN_AS_MODE = "login_as";
const HANDOFF_TTL_MS = 60_000;

export const isLoginAsSession = () =>
  sessionStorage.getItem(SESSION_MODE_KEY) === LOGIN_AS_MODE;

const isValidToken = (token) =>
  !!token &&
  !token.startsWith("dummy-") &&
  !token.startsWith("google-dummy-") &&
  !token.startsWith("google-register-");

const parseUser = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const getAuthToken = () => {
  if (isLoginAsSession()) {
    return sessionStorage.getItem("token");
  }
  return localStorage.getItem("token");
};

export const getAuthUser = () => {
  if (isLoginAsSession()) {
    return parseUser(sessionStorage.getItem("user"));
  }
  return parseUser(localStorage.getItem("user"));
};

export const getAuthCompanyId = () => {
  if (isLoginAsSession()) {
    return sessionStorage.getItem("company_id");
  }
  return localStorage.getItem("company_id");
};

export const loadInitialAuth = () => {
  if (window.location.pathname === "/login-as") {
    return { token: null, user: null, companyId: null };
  }

  if (isLoginAsSession()) {
    const token = sessionStorage.getItem("token");
    const user = parseUser(sessionStorage.getItem("user"));
    const companyId = sessionStorage.getItem("company_id");

    if (isValidToken(token) && user) {
      return { token, user, companyId };
    }

    clearLoginAsSession();
    return { token: null, user: null, companyId: null };
  }

  const token = localStorage.getItem("token");
  const user = parseUser(localStorage.getItem("user"));
  const companyId = localStorage.getItem("company_id");

  if (isValidToken(token) && user) {
    return { token, user, companyId };
  }

  clearLocalAuth();
  return { token: null, user: null, companyId: null };
};

export const saveLocalAuth = (token, user) => {
  localStorage.setItem("token", token);
  localStorage.setItem("user", JSON.stringify(user));
  localStorage.setItem("company_id", user.company_id);
};

export const startLoginAsSession = (token, user) => {
  sessionStorage.setItem(SESSION_MODE_KEY, LOGIN_AS_MODE);
  sessionStorage.setItem("token", token);
  sessionStorage.setItem("user", JSON.stringify(user));
  sessionStorage.setItem("company_id", user.company_id);
  resetSharedProductCache();
};

export const clearLoginAsSession = () => {
  sessionStorage.removeItem(SESSION_MODE_KEY);
  sessionStorage.removeItem("token");
  sessionStorage.removeItem("user");
  sessionStorage.removeItem("company_id");
  sessionStorage.removeItem("user_feature");
};

export const clearLocalAuth = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("company_id");
  localStorage.removeItem("user_feature");
  localStorage.removeItem("impersonator_token");
  localStorage.removeItem("impersonator_user");
};

export const storeLoginAsHandoff = (token, user) => {
  const key = `login_as_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(
    key,
    JSON.stringify({
      token,
      user,
      expires: Date.now() + HANDOFF_TTL_MS,
    }),
  );
  return key;
};

export const consumeLoginAsHandoff = (key) => {
  if (!key) return null;

  const raw = localStorage.getItem(key);
  localStorage.removeItem(key);

  if (!raw) return null;

  try {
    const data = JSON.parse(raw);
    if (!data?.token || !data?.user || Date.now() > data.expires) {
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

export const clearActiveAuth = () => {
  if (isLoginAsSession()) {
    clearLoginAsSession();
    return;
  }
  clearLocalAuth();
};

/** Prevent login-as tabs from reusing another user's cached product list */
export const resetSharedProductCache = () => {
  try {
    const key = "appState_viewProducts";
    const raw = localStorage.getItem(key);
    if (!raw) return;

    const parsed = JSON.parse(raw);
    localStorage.setItem(
      key,
      JSON.stringify({
        ...parsed,
        cachedProducts: null,
        lastFetched: null,
        cachedUserId: null,
        cachedCompanyId: null,
      }),
    );
  } catch {
    localStorage.removeItem("appState_viewProducts");
  }
};
